import { Alert, Button, Group, Stack, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useState } from "react";
import api from "../../api";
import { useCurrentUserQuery } from "../../hooks/useCurrentUserQuery";
import { getDefaultMRTRowEditModeOptions } from "../../mantine/getDefaultMRTRowEditModeOptions";
import { getMinimalMRTOptions } from "../../mantine/getMinimalMRTOptions";
import { AlertPresets } from "../../mantine/mantinePresets";
import { useToast } from "../../mantine/notifications/useToast";
import urls from "../../urls";
import type { ExtendedUser } from "../../utils/backend-types";
import { DeleteConfirmationModal } from "../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { DefaultUserPic } from "../DefaultUserPic/DefaultUserPic";
import { shareProjectToEmails } from "./ShareProjects";

interface ProjectShareWidgetProps {
  projectId: string;
  projectManagers: Array<ExtendedUser>;
  onDone?: () => void;
  onError: (error: unknown) => void;
  onShare: (newManager: ExtendedUser) => void;
}

function ProjectShareWidget({
  projectId,
  projectManagers,
  onDone,
  onError,
  onShare
}: ProjectShareWidgetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  function handleChangeEmail(e: React.ChangeEvent<HTMLInputElement>) {
    setEmailInput(e.target.value);
  }

  function handleClickSave() {
    setIsLoading(true);

    shareProjectToEmails(projectId, emailInput, onError).then((managers) => {
      managers.forEach((manager) => {
        if (manager) {
          onShare(manager);
        }
      });

      setEmailInput("");
      setIsLoading(false);

      if (onDone) {
        onDone();
      }
    });
  }

  function handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      handleClickSave();
    }
  }

  return (
    <div className="ProjectShareWidget">
      <Stack className="content" mb="md">
        <p>
          Um das Projekt mit anderen Nutzern zu teilen, geben Sie bitte deren
          E-Mail-Adresse ein.
        </p>
        <TextInput
          label="E-Mail*"
          type="text"
          value={emailInput}
          onChange={handleChangeEmail}
          onKeyUp={handleKeyUp}
        />
        <Alert color={AlertPresets.error.color} variant="outline">
          ACHTUNG: Der Nutzer erhält vollen Zugriff auf das Projekt
        </Alert>
      </Stack>
      <Group className="buttons" mt="sm">
        <Button
          color="secondary"
          disabled={!emailInput || emailInput.length === 0}
          loading={isLoading}
          type="submit"
          onClick={handleClickSave}
        >
          Teilen
        </Button>
        {onDone && (
          <Button variant="default" onClick={onDone}>
            Abbrechen
          </Button>
        )}
      </Group>
      <ProjectManagers
        managers={projectManagers}
        projectId={projectId}
        onDone={onDone}
      />
    </div>
  );
}

interface ProjectManagersProps {
  managers: Array<ExtendedUser>;
  projectId: string;
  onDone?: () => void;
}

function ProjectManagers({
  managers,
  projectId,
  onDone
}: ProjectManagersProps) {
  const { showErrorToast } = useToast();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [userIdToRevoke, setUserIdToRevoke] = useState<number | undefined>(
    undefined
  );

  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUserQuery();
  const isStaff = currentUser ? currentUser.is_staff : false;

  function isCurrentUserStaff(manager: ExtendedUser) {
    return isStaff && currentUser?.id === manager.id;
  }

  async function handleRemoveUserShare() {
    setIsDeleteLoading(true);
    if (userIdToRevoke) {
      try {
        await api.post(urls.api.projectsRevoke(projectId), {
          id: userIdToRevoke
        });
      } catch (error) {
        showErrorToast(error);
      }
    }
    queryClient.invalidateQueries({
      queryKey: ["project", { projectId }]
    });
    setUserIdToRevoke(undefined);
    setIsDeleteLoading(false);
    setDeleteModalOpen(false);

    if (onDone) {
      onDone();
    }
  }

  const table = useMantineReactTable<ExtendedUser>({
    ...getMinimalMRTOptions(),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        delete: {
          text: "Projektzugriff für diesen Nutzer entfernen",
          enableDelete: (cell) => !isCurrentUserStaff(cell.row.original),
          onClick: (row) => {
            setUserIdToRevoke(row.original.id);
            setDeleteModalOpen(true);
          }
        }
      }
    }),
    mantineTableHeadRowProps: { display: "none" },
    columns: [
      {
        accessorKey: "image",
        header: "Bild",
        Cell: ({ row }) => (
          <img
            alt={row.original.name}
            src={row.original.image || DefaultUserPic}
            style={{
              height: "40px",
              borderRadius: "50%",
              padding: "0.25rem",
              backgroundColor: "#fff",
              border: "1px solid #dee2e6",
              maxWidth: "100%"
            }}
          />
        )
      },
      { accessorKey: "name", header: "Name" }
    ],
    data: managers
  });

  return (
    <Stack gap="sm" mt="xl">
      <Title order={5}>Benutzer mit Zugriff</Title>
      <MantineReactTable table={table} />
      <DeleteConfirmationModal
        isLoading={isDeleteLoading}
        modalBodyElement={
          <p>
            Sind Sie sicher, dass Sie{" "}
            <strong>den Projektzugriff für diesen Nutzer</strong> löschen
            möchten?
          </p>
        }
        name="Projektzugriff"
        opened={deleteModalOpen}
        toggleModal={() => {
          setDeleteModalOpen(!deleteModalOpen);
        }}
        onConfirm={() => handleRemoveUserShare()}
      />
    </Stack>
  );
}

export { ProjectShareWidget };
